(() => {
  /**
   * ===================================================================
   * DYNAMIC ACCESSIBILITY FIXER SCRIPT
   * ===================================================================
   *
   * This script dynamically applies accessibility and DOM fixes to a webpage as
   * elements are loaded. It uses a MutationObserver to watch for changes
   * and applies fixes defined in the `fixesToApply` configuration array below.
   *
   * --- HOW IT WORKS ---
   * 1. The script watches the entire page for new elements.
   * 2. When a change is detected, it checks the `fixesToApply` list.
   * 3. For each fix, it looks for an element matching the `selector`.
   * 4. If a `condition` function is provided, it runs it. The action only
   * proceeds if the condition returns `true`.
   * 5. It then performs the specified `type` of action on the element.
   * 6. Once a fix has been successfully applied to an element, it's
   * removed from the list to improve performance.
   *
   * --- CONFIGURATION OBJECT PROPERTIES ---
   *
   * - selector: (Required) A CSS selector for the element to act on.
   * - type:     (Required) The type of action to perform. See types below.
   * - label:    (Optional) A string or function. Required for 'aria-label'
   * and 'label-text' types. The function receives the found
   * element and should return a string.
   * - condition: (Optional) A function that receives the found element. The
   * action only runs if the function returns `true`.
   *
   *
   * --- ACTION TYPES ---
   *
   * 1. type: 'aria-label'
   * - Purpose: Adds an `aria-label` attribute to an element.
   * - Details: Will not overwrite an existing `aria-label`.
   * - Needs: `label` (string or function).
   *
   * 2. type: 'label-text'
   * - Purpose: Adds text content to an empty `<label>` element.
   * - Details: Only applies if the label's text is currently empty.
   * - Needs: `label` (string or function).
   *
   * 3. type: 'remove-content'
   * - Purpose: Removes all inner content (text and child elements) from
   * the selected element.
   * - Details: The element itself remains on the page.
   *
   * 4. type: 'remove-element'
   * - Purpose: Completely removes the selected element from the page.
   * - Details: Use with a `condition` to avoid removing elements
   * incorrectly.
   *
   */
  const debugMode = false;

  /**
   * ===================================================================
   * Configuration Array
   * ===================================================================
   */
  let fixesToApply = [
    {
      selector: '[name="list_title[]"]',
      type: "aria-label",
      label: (element) => element.getAttribute("placeholder"),
    },
    {
      selector: '#ssw-wishlist-save-form [name="customer_email"]',
      type: "aria-label",
      label: (element) => element.getAttribute("placeholder"),
    },
    {
      selector: '.ssw-list-title-form [name="list_title_default"]',
      type: "aria-label",
      label: (element) => element.getAttribute("placeholder"),
    },
    {
      selector: '.ssw-modal-body .ssw-input-block-level',
      type: "aria-label",
      label: (element) => element.getAttribute("placeholder"),
    },
    {
      selector: "div.ssw-variant-option-value",
      type: "remove-content",
      condition: (element) => {
        const label = element.querySelector("label");
        return label && label.textContent.trim() === "";
      },
    },
    {
      selector: ".ssw-product-variants-title",
      type: "remove-element",
      condition: (element) => {
        return element.textContent.trim() === "";
      },
    },
    {
      selector: ".ssw-fave-vendor-title.ssw-vendor-link",
      type: "remove-element",
      condition: (element) => {
        return element.textContent.trim() === "";
      },
    },
    {
      selector: ".ssw-fave-product-title.ssw-product-link",
      type: "remove-element",
      condition: (element) => {
        return element.textContent.trim() === "";
      },
    },
    {
      selector: ".ssw-variant-image",
      type: "remove-element",
      condition: (element) => {
        return (element.getAttribute('src') || '').trim() === '';
      },
    },
    {
      selector: 'form.ssw-list-title-form button[type="submit"]',
      type: "aria-label",
      label: "Namen speichern",
    },
    {
      selector: '.hiddenCaptchaForm [name="email"]',
      type: "aria-label",
      label: "E-mail",
    },
    {
      selector: "select.ssw-variant-option-select",
      type: "remove-element",
      condition: (selectElement) => {
        const options = selectElement.options;
        if (options.length === 0) {
          return true;
        }
        if (
          options.length === 1 &&
          options[0].textContent.trim() === "" &&
          options[0].value.trim() === ""
        ) {
          return true;
        }
        return false;
      },
    },
  ];

  /**
   * ===================================================================
   * Generic MutationObserver Logic
   * ===================================================================
   */
  
  const log = (type, message, ...args) => {
    if (!debugMode) return;

    const prefix = '[Accessibility Fixer]';
    const styles = {
      info: "background: #007bff; color: #ffffff; font-weight: bold; padding: 2px 5px; border-radius: 3px;",
      warning:
        "background: #ffc107; color: #000000; font-weight: bold; padding: 2px 5px; border-radius: 3px;",
      error:
        "background: #dc3545; color: #ffffff; font-weight: bold; padding: 2px 5px; border-radius: 3px;",
      success:
        "background: #28a745; color: #ffffff; font-weight: bold; padding: 2px 5px; border-radius: 3px;",
    };

    const style = styles[type] || '';
    
    console.log(`%c${prefix}`, style, message, ...args);
  };
  
  const performAction = (element, fix) => {
    let labelText = "";
    if (typeof fix.label === "function") {
      labelText = fix.label(element);
    } else if (fix.label) {
      labelText = fix.label;
    }

    switch (fix.type) {
      case "aria-label":
        if (!element.hasAttribute("aria-label")) {
          element.setAttribute("aria-label", labelText);
          log('success', `Applied aria-label "${labelText}" to:`, element);
        }
        break;

      case "label-text":
        if (element.textContent.trim() === "") {
          element.textContent = labelText;
          log('success', `Applied text content "${labelText}" to <label>:`, element);
        }
        break;

      case "remove-content":
        element.textContent = "";
        log('success', `Removed content from:`, element);
        break;

      case "remove-element":
        log('success', "Removing element:", element);
        element.remove();
        break;

      default:
        log('error', `Unknown fix type "${fix.type}"`);
    }
  };

  const applyAccessibilityFixes = (mutationsList, observer) => {
    if (fixesToApply.length === 0) return;

    const fixesToCheck = [...fixesToApply];

    fixesToCheck.forEach((fix, index) => {
      const element = document.querySelector(fix.selector);
      if (!element) return;

      let conditionMet = false;

      if (fix.condition && typeof fix.condition === "function") {
        conditionMet = fix.condition(element);
      } else {
        conditionMet = true;
      }

      if (conditionMet) {
        performAction(element, fix);
        fixesToApply = fixesToApply.filter((f) => f !== fix);
      }
    });

    if (fixesToApply.length === 0) {
      log('info', "All dynamic accessibility fixes applied. Observer disconnecting.");
      observer.disconnect();
    }
  };
  
  // Standard MutationObserver setup
  const config = { childList: true, subtree: true };
  const observer = new MutationObserver(applyAccessibilityFixes);
  observer.observe(document.body, config);

  // Run once on initial load
  log('info', 'Initialising', fixesToApply);
  applyAccessibilityFixes([], observer);
})();